// @noflow

import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import { classNames } from "../../../lib/Util";
import { getBackend } from "../../../lib/backend/Backend2";
import { translate } from "../../../components/IntlProviderWrapper";
import { LINE_TAGS, LINE_TAGS_ACCESS, LINE_TAGS_SETTER } from "@/lib/ManufacturingTags.generated";
import ErrorComponent from "../../../components/ErrorComponent";

import type { ILineData } from "../../../lib/backend/manufacturing2.generated.types";

/**
 * Gets capacity of the line as percentage.
 * It is converted from the fractional value stored in the `capacity_factor` tag.
 */
export const getLineCapacity = (line: ILineData): number => {
    return Number((LINE_TAGS_ACCESS.capacity_factor(line.tags) * 100).toFixed(10));
};

/**
 * Updates the capacity of the line in place.
 * Given line capacity percentage is converted to a fractional value and stored as a `capacity_factor` tag.
 */
const updateLineCapacity = (line: ILineData, capacity: number | null): void => {
    if (capacity !== null && capacity !== 100) {
        LINE_TAGS_SETTER.capacity_factor(line.tags, capacity / 100);
    } else {
        delete line.tags[LINE_TAGS.capacity_factor];
    }
};

const isValidLineCapacity = (capacity: number | null): boolean => {
    // Capacity can be any valid number greater than or equal to 0
    return capacity !== null && Number.isFinite(capacity) && capacity >= 0;
};

const isValidLinePriority = (priority: number | null): boolean => {
    // Priority can be any valid number or null
    return priority === null || Number.isFinite(priority);
};

const parseEventValue = (event: React.ChangeEvent<HTMLInputElement>): number | null => {
    const value = event.currentTarget.valueAsNumber;
    return Number.isFinite(value) ? value : null;
};

type PreplanningEditLineParametersModalProps = {
    show: boolean;
    line: ILineData | null;
    onSave: (line: ILineData) => void;
    onClose: () => void;
};

const PreplanningEditLineParametersModal = ({
    show,
    line,
    onSave,
    onClose,
}: PreplanningEditLineParametersModalProps) => {
    const [error, setError] = useState<string>("");
    const [line_capacity, setLineCapacity] = useState<number | null>(null);
    const [line_priority, setLinePriority] = useState<number | null>(null);
    const [is_saving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        setError("");
        setLineCapacity(line && getLineCapacity(line));
        setLinePriority(line && line.priority);
    }, [line]);

    const handleLineCapacityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseEventValue(event);
        setLineCapacity(value);
        setError("");
    };

    const handleLinePriorityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseEventValue(event);
        setLinePriority(value);
        setError("");
    };

    const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (line === null) {
            return;
        }

        // Apply the changes to the line data
        const updated_line: ILineData = {
            ...line,
            tags: { ...line.tags },
            priority: line_priority,
        };

        updateLineCapacity(updated_line, line_capacity);

        // Save the updated line data
        setIsSaving(true);

        try {
            await getBackend().manufacturing.updateLine({
                line_uuid: updated_line.uuid,
                title: updated_line.title,
                external_id: updated_line.external_id,
                external_id2: updated_line.external_id2 ?? undefined,
                priority: updated_line.priority,
                weight: updated_line.weight,
                tags: updated_line.tags,
            });

            // Report the updated line to the parent component
            onSave(updated_line);
        } catch (error: unknown) {
            setError(translate("common.error_save", "An error occurred while saving data"));
            console.error("Saving the updated line failed:", error);
        }

        setIsSaving(false);
    };

    const form_id = "preplanning-edit-line-parameters-form";
    const capacity_input_id = `${form_id}-capacity`;
    const priority_input_id = `${form_id}-priority`;
    const is_form_valid = isValidLineCapacity(line_capacity) && isValidLinePriority(line_priority);
    const is_form_disabled = is_saving || line === null;
    const is_save_disabled = !is_form_valid || is_form_disabled;

    return (
        <Modal dialogClassName="modal-dialog-scrollable" show={show} animation={false} onHide={onClose}>
            <Modal.Header>
                <Modal.Title>
                    {translate("Preplanning.edit_line_parameters", "Edit line parameters")}
                    {line !== null ? ` - ${line.title}` : ""}
                </Modal.Title>
                <button type="button" className="close" onClick={onClose}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{translate("common.close", "Close")}</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <form id={form_id} action="" method="post" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor={capacity_input_id}>{translate("common.capacity", "Capacity")}</label>
                        <div className="input-group">
                            <input
                                type="number"
                                className={classNames("form-control", {
                                    "is-invalid": !isValidLineCapacity(line_capacity),
                                })}
                                id={capacity_input_id}
                                name="line_capacity"
                                value={line_capacity ?? ""}
                                onChange={handleLineCapacityChange}
                                min={0}
                                step="any"
                                disabled={is_form_disabled}
                                autoFocus
                            />
                            <div className="input-group-append">
                                <span className="input-group-text">%</span>
                            </div>
                        </div>
                    </div>
                    <div className="form-group mb-0">
                        <label htmlFor={priority_input_id}>{translate("common.priority", "Priority")}</label>
                        <input
                            type="number"
                            className={classNames("form-control", {
                                "is-invalid": !isValidLinePriority(line_priority),
                            })}
                            id={priority_input_id}
                            name="line_priority"
                            value={line_priority ?? ""}
                            onChange={handleLinePriorityChange}
                            disabled={is_form_disabled}
                        />
                    </div>
                </form>
                {error && <ErrorComponent className="mt-4 mb-0" type="error" msg={error} />}
            </Modal.Body>
            <Modal.Footer>
                <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={onClose}
                    disabled={is_form_disabled}
                >
                    {translate("common.cancel", "Cancel")}
                </button>
                <button className="btn btn-primary" type="submit" form={form_id} disabled={is_save_disabled}>
                    {translate("common.save", "Save")} {is_saving && <i className="fas fa-spinner fa-spin ml-1" />}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default PreplanningEditLineParametersModal;
